import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, GitBranch, Trash2 } from "lucide-react";
import {
  useNodeGroup,
  useUpdateNodeGroup,
  useDeleteNodeGroup,
} from "../../hooks/useNodeGroups";
import { useNodes } from "../../hooks/useNodes";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { nodeGroupApi } from "../../services/api";
import { NodeGroupTransition } from "../../types/node_group";
import { useScripts } from "../../hooks/useScripts";
// import TestingConfigPanel from '../../components/testing/TestingConfigPanel';
import { toast } from "react-hot-toast";
import api from "../../services/api";
import FlowToolbar from "../../components/flows/FlowToolbar";
import { usePanZoom } from "../../hooks/usePanZoom";
import NodeGraph, { NodePosition } from "../../components/flows/NodeGraph";
import NodeEditorSidebar from "../../components/flows/NodeEditorSidebar";
import NodeFiltersPanel from "../../components/nodes/NodeFiltersPanel";
import NodeCard from "../../components/nodes/NodeCard";
import AuditInfo from "../../components/ui/AuditInfo";
import { Input } from "../../components/ui/Input";
import { auditApi } from "../../services/api";

export default function NodeGroupDetailPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const id = groupId ? parseInt(groupId, 10) : 0;
  const navigate = useNavigate();
  const deleteGroupMutation = useDeleteNodeGroup();
  const {
    data: group,
    isLoading: groupLoading,
    error: groupError,
  } = useNodeGroup(id);
  const updateGroupMutation = useUpdateNodeGroup();
  const [viewMode, setViewMode] = React.useState<"tree" | "list">("tree");
  const [search, setSearch] = React.useState("");
  const [inputType, setInputType] = React.useState("");
  const [messageSearch, setMessageSearch] = React.useState("");
  const {
    nodes,
    loading: nodesLoading,
    createNode,
    updateNode,
    refreshNodes,
    deleteNode,
    total,
  } = useNodes({
    nodeGroupId: id,
    fetchAll: true,
    pageSize: 200,
    search: search || undefined,
    inputType: inputType || undefined,
  });
  const scriptsQuery = useScripts({ page: 1, page_size: 200 });
  const scripts = scriptsQuery.data?.scripts || [];

  const { zoom, pan, isPanning, onCanvasMouseDown, setZoom, resetView } =
    usePanZoom(1);
  const [positions, setPositions] = React.useState<NodePosition[]>([]);
  const [transitions, setTransitions] = React.useState<NodeGroupTransition[]>(
    [],
  );
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [editingNode, setEditingNode] = React.useState<any | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasUnsavedPositions, setHasUnsavedPositions] = React.useState(false);
  const storageKey = `nodeGroupPositions:${id}`;
  const [focusedPair, setFocusedPair] = React.useState<{
    fromId: number;
    toId: number;
  } | null>(null);
  const idToNameMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    (nodes || []).forEach((n: any) => {
      map[n.id] = n.node_name;
    });
    return map;
  }, [nodes]);

  // Local form state for group info
  const [groupName, setGroupName] = React.useState("");
  const [groupDescription, setGroupDescription] = React.useState<string>("");
  const [isUpdatingGroup, setIsUpdatingGroup] = React.useState(false);

  React.useEffect(() => {
    if (group) {
      setGroupName(group.name || "");
      setGroupDescription(group.description || "");
    }
  }, [group]);

  const handleUpdateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setIsUpdatingGroup(true);
    try {
      await updateGroupMutation.mutateAsync({
        id,
        data: { name: groupName, description: groupDescription || undefined },
      });
    } catch (_) {
      // error toasts handled in hook
    } finally {
      setIsUpdatingGroup(false);
    }
  };

  const handleDeleteGroup = async () => {
    const confirmed = window.confirm(
      "Delete this global flow? This action cannot be undone.",
    );
    if (!confirmed) return;
    try {
      await deleteGroupMutation.mutateAsync(id);
      toast.success("Global Flow deleted");
      navigate("/node-groups");
    } catch (e) {
      // error toast handled in hook
    }
  };

  const sanitizePositions = React.useCallback(
    (arr: NodePosition[]): NodePosition[] => {
      return arr.map((p) => ({
        id: p.id,
        x: Math.max(0, p.x),
        y: Math.max(0, p.y),
      }));
    },
    [],
  );

  const reloadTransitions = React.useCallback(async () => {
    try {
      const res = await nodeGroupApi.getGroupTransitions(id);
      setTransitions(res.transitions || []);
    } catch {}
  }, [id]);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await nodeGroupApi.getGroupTransitions(id);
        setTransitions(res.transitions || []);
      } catch {}
    })();
  }, [id]);

  // Load saved positions from backend; if none, fallback to localStorage; else grid init
  const loadPositions = React.useCallback(async () => {
    try {
      const resp = await api.get(`/node-groups/${id}/node-positions`);
      const serverPositions: NodePosition[] = resp.data?.positions || [];
      if (serverPositions.length > 0) {
        setPositions(sanitizePositions(serverPositions));
        setHasUnsavedPositions(false);
        return;
      }
    } catch {}
    // Fallback to local storage
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed: NodePosition[] = JSON.parse(saved);
        setPositions(sanitizePositions(parsed));
        setHasUnsavedPositions(true);
        return;
      }
    } catch {}
    // Else initialize grid after nodes load (handled in effect below)
  }, [id, storageKey, sanitizePositions]);

  React.useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  React.useEffect(() => {
    // Initialize missing positions into a grid
    if (!nodes || nodes.length === 0) return;
    setPositions((prev) => {
      const have = new Set(prev.map((p) => p.id));
      const next = [...prev];
      let idx = 0;
      for (const n of nodes) {
        if (!have.has(n.id)) {
          const col = idx % 4;
          const row = Math.floor(idx / 4);
          next.push({ id: n.id, x: 80 + col * 260, y: 80 + row * 160 });
          idx += 1;
        }
      }
      return next;
    });
  }, [nodes]);

  const save = async () => {
    setIsSaving(true);
    try {
      const sanitized = sanitizePositions(positions);
      await api.put(`/node-groups/${id}/node-positions`, {
        positions: sanitized,
      });
      localStorage.setItem(storageKey, JSON.stringify(sanitized));
      setPositions(sanitized);
      setHasUnsavedPositions(false);
      toast.success("Node positions saved");
    } catch (e) {
      toast.error("Failed to save node positions");
    } finally {
      setIsSaving(false);
    }
  };

  const openSidebarForNode = (node: any) => {
    setEditingNode(node);
    setIsCreating(false);
    setSidebarOpen(true);
  };
  const openCreateSidebar = () => {
    setEditingNode(null);
    setIsCreating(true);
    setSidebarOpen(true);
  };
  const closeSidebar = () => {
    setSidebarOpen(false);
    setEditingNode(null);
    setIsCreating(false);
  };

  const handleUpdateNode = async (data: any) => {
    if (!editingNode) return;
    setIsSubmitting(true);
    try {
      await updateNode(editingNode.id, data);
      await refreshNodes();
      setSidebarOpen(false);
    } catch (e) {
      // noop
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateNode = async (data: any) => {
    setIsSubmitting(true);
    try {
      await createNode({ ...data, node_group_id: id });
      await refreshNodes();
      setSidebarOpen(false);
      setIsCreating(false);
    } catch (e) {
      // noop
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteNode = async () => {
    if (!editingNode) return;
    const confirmed = window.confirm(
      "Delete this node? This action cannot be undone.",
    );
    if (!confirmed) return;
    setIsSubmitting(true);
    try {
      await deleteNode(editingNode.id);
      await refreshNodes();
      await reloadTransitions();
      setPositions((prev) => prev.filter((p) => p.id !== editingNode.id));
      setSidebarOpen(false);
      toast.success("Node deleted");
    } catch (e) {
      toast.error("Failed to delete node");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearFilters = () => {
    setSearch("");
    setInputType("");
    setMessageSearch("");
    setFocusedPair(null);
  };

  const handleTransitionClick = (fromId: number, toId: number) => {
    setSearch("");
    setInputType("");
    setMessageSearch("");
    setFocusedPair({ fromId, toId });
    setViewMode("list");
  };

  const filteredNodes = React.useMemo(() => {
    if (!messageSearch) return nodes;
    const q = messageSearch.toLowerCase();
    return nodes.filter((n: any) => {
      if (!n.messages) return false;
      for (const key of Object.keys(n.messages)) {
        const val = (n.messages as any)[key];
        if (typeof val === "string" && val.toLowerCase().includes(q))
          return true;
      }
      return false;
    });
  }, [nodes, messageSearch]);

  const displayNodes = React.useMemo(() => {
    if (!focusedPair) return filteredNodes;
    const allowed = new Set([focusedPair.fromId, focusedPair.toId]);
    return (nodes || []).filter((n: any) => allowed.has(n.id));
  }, [filteredNodes, focusedPair, nodes]);

  if (groupLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }
  if (groupError || !group) {
    return (
      <ErrorMessage
        message="Failed to load global flow"
        onRetry={() => {}}
        className="py-12"
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/node-groups" className="text-gray-600 hover:text-gray-800">
          <ArrowLeft className="inline h-5 w-5" /> Back
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <GitBranch className="h-6 w-6 text-blue-500" /> {group.name}
        </h1>
        <div className="ml-auto">
          <button
            onClick={handleDeleteGroup}
            className="inline-flex items-center px-3 py-1.5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </button>
        </div>
      </div>
      {group.description && (
        <p className="text-gray-600">{group.description}</p>
      )}

      {/* Group info edit section */}
      <div className="bg-white border rounded p-4">
        <h2 className="text-lg font-semibold mb-3">Global Flow Information</h2>
        <form
          onSubmit={handleUpdateGroup}
          className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end"
        >
          <div className="md:col-span-1">
            <Input
              label="Name"
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              variant="rounded-md"
              required
            />
          </div>
          <div className="md:col-span-1">
            <Input
              label="Description"
              type="text"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              variant="rounded-md"
              placeholder="Optional"
            />
          </div>
          <div className="md:col-span-1 flex gap-2 justify-start md:justify-end">
            <button
              type="button"
              onClick={() => {
                setGroupName(group.name || "");
                setGroupDescription(group.description || "");
              }}
              className="px-4 py-2 bg-gray-100 border rounded"
              disabled={isUpdatingGroup}
            >
              Reset
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded"
              disabled={isUpdatingGroup}
            >
              {isUpdatingGroup ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
      <div className="border-t border-gray-200 my-4" />

      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-md shadow-sm border border-gray-200">
          <button
            className={`px-4 py-2 text-sm font-medium ${viewMode === "tree" ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"} rounded-l-md`}
            onClick={() => setViewMode("tree")}
          >
            Tree
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${viewMode === "list" ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"} rounded-r-md border-l border-gray-200`}
            onClick={() => setViewMode("list")}
          >
            List
          </button>
        </div>
        {viewMode === "tree" && (
          <FlowToolbar
            onZoomIn={() => setZoom((z) => Math.min(2, z + 0.1))}
            onZoomOut={() => setZoom((z) => Math.max(0.3, z - 0.1))}
            onReset={resetView}
            onSave={save}
            isSaving={isSaving}
            canSave={hasUnsavedPositions}
            onAddNode={openCreateSidebar}
          />
        )}
      </div>

      {viewMode === "tree" ? (
        <NodeGraph
          nodes={nodes}
          positions={positions}
          onPositionsChange={(next) => setPositions(next)}
          onPositionsDirty={(dirty) => setHasUnsavedPositions(dirty)}
          zoom={zoom}
          panX={pan.x}
          panY={pan.y}
          onCanvasMouseDown={onCanvasMouseDown}
          transitions={transitions}
          onEditNode={(node) => {
            setEditingNode(node);
            setIsCreating(false);
            setSidebarOpen(true);
          }}
          loading={nodesLoading}
        />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {displayNodes.length} of {total} nodes
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={refreshNodes}
                disabled={nodesLoading}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                Refresh
              </button>
              <button
                onClick={openCreateSidebar}
                className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Create Node
              </button>
            </div>
          </div>

          {focusedPair && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-100 text-blue-800 px-3 py-2 rounded">
              <div className="text-sm">
                Focused on transition:{" "}
                <span className="font-medium">
                  {idToNameMap[focusedPair.fromId] ||
                    `Node ${focusedPair.fromId}`}
                </span>{" "}
                →{" "}
                <span className="font-medium">
                  {idToNameMap[focusedPair.toId] || `Node ${focusedPair.toId}`}
                </span>
              </div>
              <button
                onClick={() => setFocusedPair(null)}
                className="text-sm text-blue-700 hover:underline"
              >
                Clear
              </button>
            </div>
          )}

          <NodeFiltersPanel
            search={search}
            onSearchChange={setSearch}
            scriptId={""}
            onScriptIdChange={() => {}}
            inputType={inputType}
            onInputTypeChange={setInputType}
            globalOnly={false}
            onGlobalOnlyChange={() => {}}
            scripts={[]}
            onClearFilters={handleClearFilters}
            hideScriptFilter
            hideGlobalOnly
            messageSearch={messageSearch}
            onMessageSearchChange={setMessageSearch}
            hideMessageSearch={false}
          />

          {nodesLoading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayNodes.map((node: any) => (
                <NodeCard
                  key={node.id}
                  node={node}
                  onEdit={openSidebarForNode}
                  onDelete={() => {
                    setEditingNode(node);
                    setIsCreating(false);
                    // reuse delete from sidebar flow
                    // call handleDeleteNode via sidebar or replicate directly
                    const confirmed = window.confirm(
                      "Delete this node? This action cannot be undone.",
                    );
                    if (!confirmed) return;
                    (async () => {
                      try {
                        setIsSubmitting(true);
                        await deleteNode(node.id);
                        await refreshNodes();
                        await reloadTransitions();
                        setPositions((prev) =>
                          prev.filter((p) => p.id !== node.id),
                        );
                        setSidebarOpen(false);
                        toast.success("Node deleted");
                      } catch {
                        toast.error("Failed to delete node");
                      } finally {
                        setIsSubmitting(false);
                      }
                    })();
                  }}
                  onClone={() => {
                    // cloning not implemented for node groups yet in hook; omit
                    toast.error("Clone not available for global flows");
                  }}
                  transitions={transitions as any}
                  idToNameMap={idToNameMap}
                  onTransitionClick={handleTransitionClick}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <NodeEditorSidebar
        open={sidebarOpen && (!!editingNode || isCreating)}
        onClose={() => {
          setSidebarOpen(false);
          setEditingNode(null);
          setIsCreating(false);
        }}
        node={editingNode || undefined}
        isCreating={isCreating}
        scripts={scripts}
        nodes={nodes}
        transitions={transitions}
        onTransitionsChange={(next) => setTransitions(next)}
        onSubmit={isCreating ? handleCreateNode : handleUpdateNode}
        onDelete={!isCreating ? handleDeleteNode : undefined}
        isSubmitting={isSubmitting}
        onChange={(_, dirty) =>
          setHasUnsavedPositions(dirty || hasUnsavedPositions)
        }
        showScriptSelect={false}
        testingConfig={{ conversation_key: "", session_id: "" }}
      />
      {id ? (
        <AuditInfo
          resource="node-groups"
          id={id}
          fetch={auditApi.getLastChange}
        />
      ) : null}
    </div>
  );
}
